import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Search, Sparkles } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { useActiveClient } from '../../contexts/ActiveClientContext';
import { useAsync } from '../aai/useAsync';
import type { AaiOpportunity, OpportunityReviewState } from '../../types/aai';
import {
  Action, Divided, Drawer, EmptyState, ErrorNote, FilterChips, Kicker, ListRow, Loading,
  Panel, RouteMark, StatusChip,
} from '../../components/aa';
import { relativeTime } from './format';

/*
 * Opportunities: what could become work for this client, before it is work.
 * The first section of the Projects page, above Quick Wins and Delivery Projects.
 *
 * Orbit drafts them from the dossier, the partner refines and recommends, the
 * client decides. The partner never confirms one: sending it creates the
 * request the client's own approval gate decides, and the decision comes back
 * here with who made it and why.
 */

type FilterId = 'review' | 'awaiting' | 'decided' | 'all';

const FILTERS: Array<{ id: FilterId; label: string }> = [
  { id: 'review', label: 'Needs my review' },
  { id: 'awaiting', label: 'With the client' },
  { id: 'decided', label: 'Decided' },
  { id: 'all', label: 'All' },
];

const REVIEW_STATES: Record<FilterId, OpportunityReviewState[] | null> = {
  review: ['draft', 'needs_review', 'ready_for_client'],
  awaiting: ['awaiting_client'],
  decided: ['confirmed', 'declined', 'routed'],
  all: null,
};

/** Review state -> the chip vocabulary the console already uses. */
const STATE_CHIP: Record<OpportunityReviewState, string> = {
  draft: 'pending',
  needs_review: 'needs_review',
  ready_for_client: 'ready_short',
  awaiting_client: 'in_progress',
  confirmed: 'confirmed',
  declined: 'declined',
  routed: 'in_delivery',
};

const STATE_LABEL: Record<OpportunityReviewState, string> = {
  draft: 'Draft',
  needs_review: 'Waiting on the client',
  ready_for_client: 'Ready to send',
  awaiting_client: 'Awaiting client decision',
  confirmed: 'Confirmed by client',
  declined: 'Declined by client',
  routed: 'Routed by Orbit',
};

const NEXT: Record<OpportunityReviewState, string> = {
  draft: 'Refine the recommendation, then send',
  needs_review: 'Client owner adds what you asked for',
  ready_for_client: 'Send to the client',
  awaiting_client: 'Client owner confirms or declines',
  confirmed: 'Orbit routes it',
  declined: 'Nothing further',
  routed: 'Delivery',
};

const ORIGIN_LABEL: Record<string, string> = {
  problem: 'From the problem the client described',
  goal: 'From a stated goal',
};

const originOf = (o: AaiOpportunity): string => {
  const kind = String(o.origin?.kind || '');
  if (ORIGIN_LABEL[kind]) return ORIGIN_LABEL[kind];
  if (o.seed_source === 'plugin') return 'Scoped through the plugin';
  if (o.seed_source === 'manual' || o.seed_source === 'l1_e_deep_seed') return 'Scoped by the client owner';
  return 'Added by hand';
};

const emptyCopy = (filter: FilterId): { title: string; reason: string } => {
  if (filter === 'review') {
    return {
      title: 'Nothing waiting for your review',
      reason: 'Find opportunities reads the client’s dossier again. New answers become drafts here.',
    };
  }
  return { title: 'Nothing in this view', reason: 'Change the filter or search for a title.' };
};

const decisionLine = (o: AaiOpportunity): string | null => {
  if (!o.client_decided_at) return null;
  const who = o.client_decided_by || 'the client';
  const when = relativeTime(o.client_decided_at);
  if (o.client_decision_note) return `${who}, ${when}: “${o.client_decision_note}”`;
  return `${who}, ${when}`;
};

const ReviewDrawer: React.FC<{
  opportunity: AaiOpportunity;
  onClose: () => void;
  onChanged: () => void;
}> = ({ opportunity, onClose, onChanged }) => {
  const editable = ['draft', 'needs_review', 'ready_for_client'].includes(opportunity.review_state);
  const [title, setTitle] = useState(opportunity.title);
  const [summary, setSummary] = useState(opportunity.partner_summary || '');
  const [notes, setNotes] = useState(opportunity.partner_notes || '');
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (label: string, work: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await work();
      toast.success(label);
      onChanged();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      toast.error(detail || 'That did not save.');
    } finally {
      setBusy(false);
    }
  };

  const save = () => run('Recommendation saved.', () =>
    aaiApi.updateOpportunity(opportunity.id, { title: title.trim(), partner_summary: summary, partner_notes: notes }));
  const markReady = () => run('Marked ready to send.', () =>
    aaiApi.updateOpportunity(opportunity.id, {
      title: title.trim(), partner_summary: summary, partner_notes: notes, review_state: 'ready_for_client',
    }));
  const ask = () => {
    if (!question.trim()) {
      toast.error('Say what is missing first.');
      return;
    }
    return run('Question sent to the client owner.', () => aaiApi.requestOpportunityInfo(opportunity.id, question.trim()));
  };
  const send = () => run('Sent. The client owner decides from here.', async () => {
    await aaiApi.updateOpportunity(opportunity.id, { title: title.trim(), partner_summary: summary, partner_notes: notes });
    await aaiApi.sendOpportunity(opportunity.id);
  });

  const field = 'w-full rounded-card border border-line-strong bg-white px-4 py-3 text-[13.5px] leading-[21px] text-ink outline-none placeholder:text-ink-soft focus:border-ink';
  const label = 'mb-2 block text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft';

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>{STATE_LABEL[opportunity.review_state]}</Kicker>}
      title={opportunity.title}
      sub={originOf(opportunity)}
      footer={(
        <div className="flex flex-wrap items-center gap-3">
          {editable && <Action size="lg" onClick={send} disabled={busy} reason="Working">Send to client</Action>}
          {editable && <Action size="lg" variant="quiet" onClick={markReady} disabled={busy}>Mark ready</Action>}
          {editable && <Action size="lg" variant="quiet" onClick={save} disabled={busy}>Save</Action>}
          <Action size="lg" variant="ghost" onClick={onClose}>Close</Action>
        </div>
      )}
    >
      {opportunity.statement && (
        <div className="mb-5 rounded-panel bg-canvas-inset px-4 py-3 text-[13px] leading-[21px] text-ink-dim">
          <div className={label}>What the client said</div>
          {opportunity.statement}
        </div>
      )}

      {!editable && (
        <div className="space-y-4 text-[13.5px] leading-[21px] text-ink">
          {opportunity.partner_summary && (
            <div><div className={label}>Your recommendation</div>{opportunity.partner_summary}</div>
          )}
          {opportunity.partner_notes && (
            <div><div className={label}>Your notes</div>{opportunity.partner_notes}</div>
          )}
          {decisionLine(opportunity) && (
            <div><div className={label}>Client decision</div>{decisionLine(opportunity)}</div>
          )}
          <div><div className={label}>Orbit stage</div>{opportunity.stage || 'Not scoped yet'}</div>
        </div>
      )}

      {editable && (
        <>
          <label className="block">
            <span className={label}>Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} className={`${field} h-11`} />
          </label>
          <label className="mt-4 block">
            <span className={label}>Your recommendation</span>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={4}
              placeholder="What you propose the client does about this, in their words."
              className={`${field} resize-y`}
            />
            <span className="mt-1 block text-[12px] text-ink-dim">The client owner reads this when they decide.</span>
          </label>
          <label className="mt-4 block">
            <span className={label}>Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Context for you and the delivery team. The client does not see this."
              className={`${field} resize-y`}
            />
          </label>

          <div className="mt-6 border-t border-line-soft pt-5">
            <span className={label}>Ask the client owner for what is missing</span>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={2}
              placeholder="How many hours a week does this take today, and who does it?"
              className={`${field} resize-y`}
            />
            <div className="mt-3">
              <Action variant="quiet" size="sm" onClick={ask} disabled={busy}>Ask for information</Action>
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
};

export const OpportunitiesSection: React.FC<{
  clientName: string;
  onChanged?: () => void;
  /** Right of the head, beside Find opportunities: the page's See all link. */
  action?: React.ReactNode;
}> = ({ clientName, onChanged, action }) => {
  const { active } = useActiveClient();
  const [filter, setFilter] = useState<FilterId>('review');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [discovering, setDiscovering] = useState(false);

  const opportunities = useAsync<{ opportunities: AaiOpportunity[] }>(
    () => aaiApi.listOpportunities(), 'Could not load this client’s opportunities.', [active?.id],
  );

  const rows = useMemo(() => {
    const all = opportunities.data?.opportunities || [];
    const states = REVIEW_STATES[filter];
    const needle = query.trim().toLowerCase();
    return all
      .filter((o) => !states || states.includes(o.review_state))
      .filter((o) => !needle || o.title.toLowerCase().includes(needle) || (o.statement || '').toLowerCase().includes(needle));
  }, [opportunities.data, filter, query]);

  const counts = useMemo(() => {
    const all = opportunities.data?.opportunities || [];
    const count = (id: FilterId) => {
      const states = REVIEW_STATES[id];
      if (!states) return all.length;
      return all.filter((o) => states.includes(o.review_state)).length;
    };
    return FILTERS.map((f) => ({ ...f, count: count(f.id) }));
  }, [opportunities.data]);

  const reload = () => {
    opportunities.reload();
    if (onChanged) onChanged();
  };

  const discover = async () => {
    setDiscovering(true);
    try {
      const res = await aaiApi.discoverOpportunities();
      const created = res.data.created;
      if (created === 0) toast.info('Nothing new. Every answer in the dossier already has an opportunity.');
      if (created === 1) toast.success('1 new opportunity drafted from the dossier.');
      if (created > 1) toast.success(`${created} new opportunities drafted from the dossier.`);
      opportunities.reload();
    } catch {
      toast.error('Could not read the dossier for opportunities.');
    } finally {
      setDiscovering(false);
    }
  };

  const open = (opportunities.data?.opportunities || []).find((o) => o.id === openId) || null;

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4 border-b border-line-soft pb-4">
        <div>
          <Kicker className="mb-1.5">Project type · Opportunity</Kicker>
          <h2 className="m-0 text-[22px] font-bold leading-[28px] tracking-[-0.01em] text-ink">{clientName} Opportunities</h2>
          <p className="m-0 mt-1.5 max-w-[720px] text-[13px] leading-[21px] text-ink-dim">
            What Orbit found in the client's answers, refined by you, decided by them. Nothing here is work until the client owner confirms it.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-4">
          <Action variant="quiet" icon={<Sparkles size={14} />} onClick={discover} disabled={discovering} reason="Reading the dossier">
            Find opportunities
          </Action>
          {action}
        </div>
      </div>

      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 md:px-6">
          <FilterChips
            tabs={counts.map((f) => ({ id: f.id, label: f.label, count: f.count }))}
            active={filter}
            onChange={(id) => setFilter(id as FilterId)}
            label="Opportunity state"
          />
          <label className="relative block w-full max-w-[320px]">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find an opportunity"
              className="h-10 w-full rounded-act border border-line-strong bg-white pl-9 pr-3 text-[13px] text-ink outline-none placeholder:text-ink-soft focus:border-ink"
            />
          </label>
        </div>

        {opportunities.loading && <Loading label="Loading opportunities" />}
        {opportunities.error && <div className="px-6 py-5"><ErrorNote message={opportunities.error} /></div>}
        {!opportunities.loading && !opportunities.error && rows.length === 0 && (
          <EmptyState title={emptyCopy(filter).title} reason={emptyCopy(filter).reason} />
        )}
        {rows.length > 0 && (
          <Divided>
            {rows.map((o) => (
              <ListRow
                key={o.id}
                mark={<RouteMark glyph="O" size="lg" />}
                kicker={originOf(o)}
                title={o.title}
                badge={<StatusChip status={STATE_CHIP[o.review_state]} label={STATE_LABEL[o.review_state]} />}
                desc={o.partner_summary || o.statement || undefined}
                meta={[
                  { label: 'Next', value: NEXT[o.review_state] },
                  { label: 'Orbit stage', value: o.stage || 'Not scoped yet' },
                  { label: 'Decision', value: decisionLine(o) || 'Not yet' },
                ]}
                onClick={() => setOpenId(o.id)}
                arrow
              />
            ))}
          </Divided>
        )}
      </Panel>

      {open && (
        <ReviewDrawer
          opportunity={open}
          onClose={() => setOpenId(null)}
          onChanged={reload}
        />
      )}
    </section>
  );
};
